import {
  MarketButton, MarketButtonGroup, MarketHeader, type MarketModalFull, MarketModalPartial, MarketDialog,
  MarketFooter,
} from '@market/react'; // eslint-disable-line square/no-legacy-market-imports
import classNames from 'classnames';
import {
  useCallback, useRef, type ReactElement, type ReactNode,
} from 'react';
import styles from './BankingModalBase.module.css';

/**
 * Base modal component that provides shared functionality for Banking modals.
 * This component handles the common structure and styling, while allowing
 * different modal wrapper components (MarketModalPartial, MarketModalFull, or MarketDialog).
 *
 * This is an internal component - use BankingModalPartial, BankingModalFull, or BankingDialog instead.
 */

type Content = string | ReactNode;

export interface NavigationButton {
  readonly icon: ReactNode;
  readonly ariaLabel?: string;
  readonly onClick: () => void;
}

export interface BankingModalBaseProps {
  readonly active: boolean;
  readonly buttons?: ReactElement<typeof MarketButton>[];
  readonly content: Content;
  readonly title?: string;
  readonly subtitle?: string;
  readonly navigationButton?: NavigationButton;
  readonly headerActions?: ReactNode;
  readonly footer?: ReactNode;
  readonly ModalWrapper: typeof MarketModalFull | typeof MarketModalPartial | typeof MarketDialog;
  readonly onModalLoaded?: () => void;
  readonly onModalClose: () => void;
}

export const BankingModalBase = ({
  active, buttons, content, title, subtitle, navigationButton, headerActions, footer, onModalLoaded, onModalClose, ModalWrapper,
}: BankingModalBaseProps) => {
  // Market may emit both header navigate and dialog dismissed for a single close action.
  // Keep the shared close callback idempotent so consumers only see one close.
  const hasClosedRef = useRef(false);

  const handleModalClose = useCallback(() => {
    if (hasClosedRef.current) {
      return;
    }

    hasClosedRef.current = true;
    onModalClose();
  }, [onModalClose]);

  const handleModalLoaded = useCallback(() => {
    // Reset the close guard when the modal is shown again on the same component instance.
    hasClosedRef.current = false;
    onModalLoaded?.();
  }, [onModalLoaded]);

  // Use stack alignment for partial modals, fill for dialogs, left alignment for full modals
  const buttonAlignment: 'stack' | 'fill' | 'left' =
    ModalWrapper === MarketModalPartial
      ? 'stack' :
      ModalWrapper === MarketDialog
        ? 'fill' :
        'left';

  return active
    ? <ModalWrapper onMarketDialogDismissed={handleModalClose} onMarketDialogLoaded={handleModalLoaded}>
      <MarketHeader className={styles.header} data-testid="header" onMarketHeaderNavigate={handleModalClose}>
        {title && <h2>{title}</h2>}
        {subtitle && <p slot="subheading" className="paragraph-30">{subtitle}</p>}
        {navigationButton && (
          <MarketButton
            rank="secondary"
            slot="navigation"
            data-testid="banking-modal-custom-navigation-button"
            aria-label={navigationButton.ariaLabel}
            onClick={navigationButton.onClick}
          >
            <span slot="icon" data-testid="banking-modal-custom-navigation-button-icon">{navigationButton.icon}</span>
          </MarketButton>
        )}
        {headerActions && <div slot="actions">{headerActions}</div>}
      </MarketHeader>
      <section className="main">
        {typeof content === 'string' ?
          <p className={classNames('paragraph-30', styles.description)}>{content}</p>
          : content}
        {buttons && buttons.length > 0 && (
          <MarketButtonGroup className={styles.buttons} alignment={buttonAlignment}>
            {buttons.map((button) => button)}
          </MarketButtonGroup>
        )}
        {footer && (
          <MarketFooter>
            {footer}
          </MarketFooter>
        )}
      </section>
    </ModalWrapper>
    : null;
};
